<template>
  <div class="absolute" :class="[showIframe ? ' top-20 right-9' : ' top-0 right-1']">
    <!-- Chat Iframe -->
    <div v-if="showIframe" class="relative bg-white rounded-lg p-2" :style="chatPosition">
      <div class="absolute top-2 right-6 text-lg cursor-pointer text-pdsPrimaryColor" @click="closeIframeChat">X</div>
      <div class="absolute top-2 left-2 text-lg cursor-move text-pdsPrimaryColor" @mousedown="startDrag">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="9" cy="5" r="1"></circle>
          <circle cx="9" cy="12" r="1"></circle>
          <circle cx="9" cy="19" r="1"></circle>
          <circle cx="15" cy="5" r="1"></circle>
          <circle cx="15" cy="12" r="1"></circle>
          <circle cx="15" cy="19" r="1"></circle>
        </svg>
      </div>
      <iframe
        :src="iframeURL"
        frameborder="1"
        class="rounded-lg w-auto min-w-96 mt-2"
        :class="[showSkillAgentStatus ? 'h-32' : 'h-96']"
      />
    </div>

    <!-- Treatment Areas Selection -->
    <div v-if="showTreatmentAreas" class="absolute right-8 top-20 bg-white rounded-2xl w-64 p-6">
      <template v-if="chatDetails.isChatAvailable">
        <h3 class="text-black font-semibold m-0 mb-2 text-base">Waar gaat de vraag over?</h3>
        <div class="grid gap-2">
          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <MenuItems
              class="absolute right-0 z-10 origin-top-right rounded-md bg-white py-1 shadow-lg ring-black ring-opacity-5 focus:outline-none w-64"
            >
              <MenuItem
                v-for="treatmentArea in filteredTreatmentAreas"
                :key="treatmentArea"
                @click.prevent="setTreatmentArea(treatmentArea)"
              >
                <a
                  :class="[
                    'block px-6 py-4 text-sm text-pdsPrimaryColor hover:bg-gray-100 cursor border-t border-solid border-gray-200'
                  ]"
                >
                  {{ treatmentArea }}
                </a>
              </MenuItem>
            </MenuItems>
          </transition>
        </div>
      </template>
      <h4 v-else class="text-black m-0 font-semibold">{{ chatDetails.chatStatusMessage }}</h4>
    </div>

    <!-- Skill Agent Status Message -->
    <div v-if="showSkillAgentStatus" class="bg-white p-4 rounded-lg shadow-lg mt-4">
      <h4 class="text-black m-0 font-semibold">
        Op dit moment is er helaas geen geschikte medewerker beschikbaar, probeer het later nog eens
      </h4>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { activeDepartment$ } from '@/services';
import { computed, ref, onMounted, onBeforeUnmount, getCurrentInstance, onBeforeMount } from 'vue';
import { useStore } from 'vuex';

export default {
  name: 'PrescriberChat',
  setup() {
    const instance = getCurrentInstance();
    const $emitter = instance.appContext.config.globalProperties.$emitter;
    const store = useStore();

    // Reactive state
    const selectedDepartment = ref(null);
    const selectedTreatmentArea = ref(null);
    const showSkillAgentStatus = ref(false);
    const showTreatmentAreas = ref(false);
    const iframeURL = ref(null);
    const chatDetails = ref({
      isChatAvailable: false,
      chatStatusMessage: '',
      treatmentAreas: [],
      userExternalID: '',
      userEmail: '',
      userFullName: '',
      noSkillAgentMessage: ''
    });

    // Add position state
    const chatPosition = ref({
      position: 'fixed',
      top: '100px',
      right: '20px',
      zIndex: 1000
    });

    // Computed properties
    const patientDetails = computed(() => store.getters['patient/getPatientGegevens']);
    const showIframe = computed(() => store.getters['prescriber/getPrescriberChatIframe']);
    const filteredTreatmentAreas = computed(() => {
      return chatDetails.value.treatmentAreas.filter(
        (area) => area !== 'Infuus' && area !== 'Preventie' && area !== 'Farmaceutica' && area !== 'Respiratoir'
      );
    });

    // Store actions & mutations
    const setPrescriberChatIframe = (value) => store.commit('prescriber/setPrescriberChatIframe', value);
    const fetchChatStatusMessage = () => store.dispatch('prescriber/fetchChatStatusMessage');
    const setChatStatus = (value) => store.commit('navigation/setChatStatus', value);

    // Subscription for active department updates
    activeDepartment$.subscribe((value) => {
      selectedDepartment.value = value;
    });

    // Methods
    const checkTreatmentAreas = () => {
      if (chatDetails.value.treatmentAreas.length > 1) {
        if (!showTreatmentAreas.value && !showIframe.value) {
          showTreatmentAreas.value = true;
        }
      } else {
        selectedTreatmentArea.value = chatDetails.value.treatmentAreas.value[0];
        setTreatmentArea(selectedTreatmentArea.value);
      }
    };

    const setTreatmentArea = async (treatmentArea) => {
      selectedTreatmentArea.value = treatmentArea;
      showTreatmentAreas.value = false;
      try {
        const { data } = await axios.get(`Chat/IsSkillAgentAvailable?treatmentArea=${treatmentArea}`);
        if (data.SkillAgentAvailable) {
          showPrescriberChatIframe();
        } else {
          showSkillAgentStatus.value = true;
          setTimeout(() => (showSkillAgentStatus.value = false), 5000);
        }
      } catch (error) {
        console.error('Error fetching skill agent status:', error);
      }
    };

    const showPrescriberChatIframe = () => {
      if (!selectedDepartment.value) return;
      const patientId = patientDetails.value.ExternalId || 'null';
      iframeURL.value = `https://wechat.mediq.com/directstart/WC_Voeding/nl-nl?prescriberId=${encodeURIComponent(
        chatDetails.value.userExternalID
      )}&patientId=${encodeURIComponent(patientId)}&departmenId=${encodeURIComponent(
        selectedDepartment.value.ppwRecId.split('-')[1]
      )}&email=${encodeURIComponent(chatDetails.value.userEmail)}&name=U:&naam-voorschrijver=${encodeURIComponent(
        chatDetails.value.userFullName
      )}&treatmentarea=${selectedTreatmentArea.value}`;
      setPrescriberChatIframe(!!chatDetails.value.isChatAvailable);
    };

    const closeTreatmentAreaSelection = () => {
      if (showTreatmentAreas.value) showTreatmentAreas.value = false;
    };

    const closeIframeChat = () => setPrescriberChatIframe(false);

    // Update drag handling
    const startDrag = (e) => {
      const element = e.target.closest('.relative');
      const rect = element.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const moveHandler = (e) => {
        const newX = e.clientX - x;
        const newY = e.clientY - y;

        chatPosition.value = {
          ...chatPosition.value,
          top: `${newY}px`,
          right: 'auto',
          left: `${newX}px`
        };
      };

      const upHandler = () => {
        document.removeEventListener('mousemove', moveHandler);
        document.removeEventListener('mouseup', upHandler);
      };

      document.addEventListener('mousemove', moveHandler);
      document.addEventListener('mouseup', upHandler);
    };

    onBeforeMount(async () => {
      try {
        const response = await fetchChatStatusMessage();
        Object.assign(chatDetails.value, response.data);
        setChatStatus(chatDetails.value.isChatAvailable);
        chatDetails.value.chatStatusMessage = chatDetails.value.isChatAvailable
          ? 'Chat is online. Waarmee kan ik u helpen?'
          : 'Chat is momenteel niet beschikbaar. Deze is beschikbaar op ma. t/m vr. tussen 09:00 en 17:00.';
      } catch (error) {
        console.error('Error fetching chat status:', error);
        // Set chat as unavailable on any error
        chatDetails.value.isChatAvailable = false;
        chatDetails.value.chatStatusMessage = 'Chat is momenteel niet beschikbaar.';
        setChatStatus(false);
      }
    });

    onMounted(() => {
      document.addEventListener('click', closeTreatmentAreaSelection);
      if (showIframe.value) {
        showPrescriberChatIframe();
      }
      $emitter.on('chat', () => {
        showTreatmentAreas.value = !showTreatmentAreas.value;
        if (!showTreatmentAreas.value) checkTreatmentAreas();
      });
      $emitter.on('chat-close', () => {
        showTreatmentAreas.value = false;
      });
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', closeTreatmentAreaSelection);
    });

    return {
      chatDetails,
      patientDetails,
      showIframe,
      setPrescriberChatIframe,
      fetchChatStatusMessage,
      selectedDepartment,
      closeTreatmentAreaSelection,
      showTreatmentAreas,
      showSkillAgentStatus,
      iframeURL,
      selectedTreatmentArea,
      checkTreatmentAreas,
      setTreatmentArea,
      showPrescriberChatIframe,
      setChatStatus,
      closeIframeChat,
      filteredTreatmentAreas,
      chatPosition,
      startDrag
    };
  }
};
</script>
